import type { BossDto } from '../../lib/reference';
import { difficultyOptionLabel, ineligibilityReason, type CharacterForce } from '../../lib/bossGrouping';
import { IconCoin } from '../icons';
import { Stamp } from '../Stamp';

export interface BossCellPlan {
  difficulty: string;
}

export interface BossCellClear {
  difficulty: string;
  crystalSold: boolean;
  crystalValue: number | null;
}

interface BossCellProps {
  boss: BossDto;
  character: CharacterForce;
  characterName: string;
  plan: BossCellPlan | undefined;
  clear: BossCellClear | undefined;
  /** False when this cell cannot be edited right now (e.g. a daily boss outside the current week). */
  editable: boolean;
  planPending: boolean;
  clearPending: boolean;
  /**
   * `5` on a Heroic account, `1` otherwise (see `crystalMultiplierForWorldType`). Applied to the
   * catalog's base crystal value before formatting the difficulty-option label — the full,
   * unsplit price players compare against each other, just scaled for their world's economy.
   */
  crystalMultiplier: number;
  onSetDifficulty: (difficulty: string) => void;
  onSetCleared: (cleared: boolean) => void;
  onSetCrystalSold: (crystalSold: boolean) => void;
}

/**
 * One character x boss intersection: a difficulty select (WP4 D15 — this is the *only* place a
 * difficulty is chosen; a clear always inherits it), a clear checkbox, and a "sold" toggle that
 * only means anything once cleared. Reused as-is by both the desktop grid and the mobile accordion.
 */
export function BossCell({
  boss,
  character,
  characterName,
  plan,
  clear,
  editable,
  planPending,
  clearPending,
  crystalMultiplier,
  onSetDifficulty,
  onSetCleared,
  onSetCrystalSold,
}: BossCellProps) {
  const cleared = clear !== undefined;
  const selectDisabled = !editable || planPending;
  const clearDisabled = !editable || clearPending || (!cleared && plan === undefined);

  return (
    <div className="flex flex-row flex-wrap items-center justify-center gap-2 py-1">
      <select
        aria-label={`${boss.name} difficulty for ${characterName}`}
        value={plan?.difficulty ?? ''}
        disabled={selectDisabled}
        onChange={(e) => onSetDifficulty(e.target.value)}
        className="min-h-tap w-full max-w-[10.5rem] rounded-md border border-border-strong bg-bg-elevated-hover px-1.5 text-xs text-text transition-colors focus:border-accent focus:outline-none disabled:opacity-50"
      >
        <option value="" disabled>
          Set difficulty
        </option>
        {boss.difficulties.map((d) => {
          const reason = ineligibilityReason(character, d);
          return (
            <option key={d.difficulty} value={d.difficulty} disabled={reason !== null} title={reason ?? undefined}>
              {difficultyOptionLabel(
                d.difficulty,
                d.crystalValue === null ? null : d.crystalValue * crystalMultiplier,
              )}
              {reason ? ' ⚠' : ''}
            </option>
          );
        })}
      </select>

      <div className="flex items-center gap-2">
        <div title={clearDisabled && !editable ? undefined : plan === undefined ? 'Set a difficulty first' : undefined}>
          <Stamp
            checked={cleared}
            disabled={clearDisabled}
            ariaLabel={`${boss.name} cleared for ${characterName}`}
            onChange={onSetCleared}
          />
        </div>
        {cleared && (
          <label
            className="flex min-h-tap min-w-tap cursor-pointer items-center justify-center gap-1 text-[0.625rem] text-text-faint"
            title="Crystal sold"
          >
            <input
              type="checkbox"
              checked={clear?.crystalSold ?? false}
              disabled={!editable || clearPending}
              aria-label={`${boss.name} crystal sold for ${characterName}`}
              onChange={(e) => onSetCrystalSold(e.target.checked)}
              className="size-4 rounded border-border-strong bg-bg-elevated-hover accent-[var(--crystal)] disabled:opacity-40"
            />
            <IconCoin className={clear?.crystalSold ? 'text-crystal' : undefined} />
          </label>
        )}
      </div>
    </div>
  );
}
